import {
  GlobalSearchContactResult,
  GlobalSearchCompanyResult,
  GlobalSearchRequestResult,
  SearchResult as FullSearchResult,
  Contact,
  Company,
  Request,
} from "types";

// Type for the search results used in this component
export type ComponentSearchResult = GlobalSearchContactResult | GlobalSearchCompanyResult | GlobalSearchRequestResult;

// Type guard to check if a result is a valid search result for this component
export function isValidSearchResult(result: unknown): result is ComponentSearchResult {
  if (!result || typeof result !== 'object') {
    return false;
  }
  
  const typedResult = result as { __typename?: string };
  return typedResult.__typename === 'GlobalSearchContactResult' || 
         typedResult.__typename === 'GlobalSearchCompanyResult' ||
         typedResult.__typename === 'GlobalSearchRequestResult';
}

// Helper to convert from full SearchResult union to component-specific result
// This handles the case where RecentSearch.searchResult is a full Contact/Company entity
export function toComponentSearchResult(result: FullSearchResult): ComponentSearchResult | null {
  // Since the SearchResult union includes full entities (Contact, Company, etc)
  // but not GlobalSearch types, we need to convert them
  
  // Convert full Contact to GlobalSearchContactResult format
  if (result.__typename === 'Contact') {
    const contact = result as Contact;
    const globalSearchContact: GlobalSearchContactResult = {
      __typename: 'GlobalSearchContactResult',
      contactId: contact.id,
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email,
      mobilePhone: contact.mobilePhone,
      company: contact.company?.name || null,
      relatedTrips: [],
    };
    return globalSearchContact;
  }
  
  // Convert full Company to GlobalSearchCompanyResult format
  if (result.__typename === 'Company') {
    const company = result as Company;
    const globalSearchCompany: GlobalSearchCompanyResult = {
      __typename: 'GlobalSearchCompanyResult',
      companyId: company.id,
      name: company.name,
      email: company.email,
      phoneNumber: company.phoneNumber,
      relatedTrips: [],
    };
    return globalSearchCompany;
  }
  
  // Convert full Request to GlobalSearchRequestResult format
  if (result.__typename === 'Request') {
    const request = result as Request;
    // For recent searches, we need to create a minimal GlobalSearchRequestResult
    // Note: Some fields like dateTime and dateString won't be available from the Request entity
    const globalSearchRequest: GlobalSearchRequestResult = {
      __typename: 'GlobalSearchRequestResult',
      requestId: request.id,
      orderNumber: request.orderNumber,
      // These fields would need to be populated from the first trip if available
      tripId: request.trips?.[0]?.id || null,
      tripNumber: request.trips?.[0]?.tripNumber || null,
      dateTime: null, // Not available in Request entity
      dateString: null, // Not available in Request entity
    };
    return globalSearchRequest;
  }
  
  // For other types (Driver, Trip), return null
  return null;
}